/**
 * Extracts an embeddable Google Maps URL from a raw address or iframe string.
 * Supports iframe `src` extraction, direct URLs, and plain text addresses.
 * 
 * @param {string} raw - The raw map URL, iframe string, or address from the backend.
 * @returns {string} - An embeddable Google Maps URL.
 */
export const extractMapUrl = (raw) => {
  if (!raw) return '';
  const val = String(raw).trim();

  // If backend provided an entire iframe string, extract its src
  const iframeMatch = val.match(/src=["']([^"']+)["']/i);
  const srcCandidate = iframeMatch ? iframeMatch[1] : val;
  
  const buildGoogleEmbed = (querySource) =>
    `https://www.google.com/maps?q=${encodeURIComponent(querySource)}&output=embed`;

  // If it's already a URL
  if (/^https?:\/\//i.test(srcCandidate)) {
    try {
      const url = new URL(srcCandidate);
      const hostname = url.hostname.toLowerCase();
      const looksGoogle =
        hostname.includes('google.') ||
        hostname.endsWith('goo.gl') ||
        hostname.includes('maps.app') ||
        hostname === 'share.google';

      // If it's a Google Maps URL (including share links), ensure it's embeddable
      if (looksGoogle) {
        if (srcCandidate.includes('/embed')) return srcCandidate;
        const q =
          url.searchParams.get('q') ||
          url.searchParams.get('query') ||
          url.searchParams.get('destination') ||
          '';
        const query = q || val;
        return buildGoogleEmbed(query);
      }

      // Non-Google providers: try using as-is
      return srcCandidate;
    } catch (e) {
      const fallback = val || srcCandidate;
      return buildGoogleEmbed(fallback);
    }
  }

  // Otherwise assume it's a plain address; build a Google Maps embed URL
  return buildGoogleEmbed(val);
};
